import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as VibecityLogo } from "@/assets/vibecity-logo.svg";

/**
 * AdminShell — sidebar + topbar layout for the admin dashboard.
 *
 * Props:
 *  - user: { name, email, picture }
 *  - currentKey: active nav key
 *  - onNavigate(key): nav change handler
 *  - onLogout(): logout handler
 *  - children: tab content
 */
export default function AdminShell({
    user,
    currentKey,
    onNavigate,
    onLogout,
    children,
}) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    // Close mobile drawer on nav change
    useEffect(() => {
        setMobileOpen(false);
    }, [currentKey]);

    // Persist sidebar collapse preference
    useEffect(() => {
        const saved = localStorage.getItem("vc_admin_sidebar_collapsed");
        if (saved === "1") setCollapsed(true);
    }, []);
    useEffect(() => {
        localStorage.setItem(
            "vc_admin_sidebar_collapsed",
            collapsed ? "1" : "0",
        );
    }, [collapsed]);

    const sidebarW = collapsed ? 72 : 248;

    return (
        <div
            data-testid="admin-shell"
            className="min-h-screen bg-[#0a0a0a] text-ink flex"
        >
            {/* Mobile backdrop */}
            {mobileOpen && (
                <button
                    aria-label="Close menu"
                    onClick={() => setMobileOpen(false)}
                    className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                />
            )}

            {/* Sidebar */}
            <aside
                data-testid="admin-sidebar"
                className={`fixed lg:static inset-y-0 left-0 z-50 transition-transform lg:transition-[width] duration-200 ease-out
                ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
                shrink-0 bg-[#0d0d0d] border-r border-line flex flex-col`}
                style={{ width: sidebarW }}
            >
                {/* Logo / collapse toggle */}
                <div className="px-4 py-5 flex items-center justify-between border-b border-line">
                    <Link
                        to="/"
                        data-testid="admin-home-link"
                        className="flex items-center gap-3 min-w-0"
                    >
                        <VibecityLogo className="h-5 w-auto shrink-0" />
                        {!collapsed && (
                            <span
                                className="text-[0.62rem] tracking-[0.22em] uppercase text-ink-soft font-display font-bold truncate"
                            >
                                / Admin
                            </span>
                        )}
                    </Link>
                    <button
                        type="button"
                        onClick={() => setCollapsed((c) => !c)}
                        className="hidden lg:grid place-items-center w-7 h-7 rounded-md text-ink-soft hover:text-ink hover:bg-white/5 transition-colors"
                        data-testid="sidebar-collapse"
                        aria-label="Toggle sidebar width"
                    >
                        {collapsed ? "›" : "‹"}
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-6">
                    {NAV_GROUPS.map((g) => (
                        <NavGroup
                            key={g.label}
                            label={g.label}
                            items={g.items}
                            collapsed={collapsed}
                            currentKey={currentKey}
                            onNavigate={onNavigate}
                        />
                    ))}
                </nav>

                {/* User */}
                {user && (
                    <div className="border-t border-line p-3">
                        <div
                            className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}
                        >
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt=""
                                    className="w-9 h-9 object-cover rounded-full border border-line shrink-0"
                                />
                            ) : (
                                <div className="w-9 h-9 grid place-items-center bg-white/10 text-sm font-display rounded-full shrink-0">
                                    {user.name?.[0] || "?"}
                                </div>
                            )}
                            {!collapsed && (
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-display font-bold leading-none truncate">
                                        {user.name}
                                    </div>
                                    <div className="text-[0.62rem] text-ink-soft mt-1 truncate">
                                        {user.email}
                                    </div>
                                </div>
                            )}
                            {!collapsed && (
                                <button
                                    type="button"
                                    onClick={onLogout}
                                    data-testid="admin-logout"
                                    title="Sign out"
                                    className="text-ink-soft hover:text-ink transition-colors w-8 h-8 grid place-items-center rounded-md hover:bg-white/5"
                                    aria-label="Sign out"
                                >
                                    <LogoutIcon />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                {/* Mobile topbar */}
                <header className="lg:hidden sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-line px-4 h-14 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={() => setMobileOpen(true)}
                        data-testid="admin-mobile-menu"
                        className="grid place-items-center w-9 h-9 rounded-md hover:bg-white/5"
                        aria-label="Open menu"
                    >
                        <MenuIcon />
                    </button>
                    <span className="font-display font-bold tracking-[0.15em] uppercase text-sm text-ink">
                        {labelFor(currentKey)}
                    </span>
                    <Link
                        to="/"
                        data-testid="admin-back-home"
                        className="text-[0.65rem] tracking-[0.18em] uppercase text-ink-soft hover:text-ink"
                    >
                        Site →
                    </Link>
                </header>

                <main className="flex-1 px-4 md:px-8 lg:px-10 py-6 md:py-10 overflow-x-hidden">
                    {children}
                </main>
            </div>
        </div>
    );
}

// ---------- Nav tree ----------
export const NAV_GROUPS = [
    {
        label: "Inbox",
        items: [
            { key: "applications", label: "Applications", icon: InboxIcon },
            { key: "host-events", label: "Host Events", icon: CalendarIcon },
            { key: "emails", label: "Emails", icon: MailIcon },
            { key: "cohorts", label: "Cohorts", icon: UsersIcon },
        ],
    },
    {
        label: "Content",
        items: [
            { key: "tiles", label: "City Sphere", icon: GridIcon },
            { key: "city-cards", label: "Orbital Cards", icon: OrbitIcon },
            { key: "playbook", label: "Playbook", icon: StackIcon },
            { key: "assets", label: "Asset Bank", icon: ImageIcon },
        ],
    },
];

export function labelFor(key) {
    for (const g of NAV_GROUPS) {
        for (const i of g.items) if (i.key === key) return i.label;
    }
    return "Admin";
}

function NavGroup({ label, items, collapsed, currentKey, onNavigate }) {
    return (
        <div>
            {!collapsed && (
                <div className="px-3 mb-2 text-[0.55rem] tracking-[0.28em] uppercase text-ink-soft/60 font-display font-bold">
                    {label}
                </div>
            )}
            <div className="space-y-0.5">
                {items.map((i) => {
                    const active = currentKey === i.key;
                    const Icon = i.icon;
                    return (
                        <button
                            key={i.key}
                            type="button"
                            data-testid={`nav-${i.key}`}
                            onClick={() => onNavigate(i.key)}
                            title={collapsed ? i.label : undefined}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm transition-colors
                                ${
                                    active
                                        ? "bg-white/[0.08] text-ink"
                                        : "text-ink-soft hover:text-ink hover:bg-white/[0.04]"
                                }`}
                        >
                            <span
                                className="shrink-0"
                                style={{
                                    color: active ? "#5fe3c9" : "currentColor",
                                }}
                            >
                                <Icon />
                            </span>
                            {!collapsed && (
                                <span className="font-display font-bold tracking-wide truncate">
                                    {i.label}
                                </span>
                            )}
                            {!collapsed && active && (
                                <span
                                    aria-hidden
                                    className="ml-auto h-1.5 w-1.5 rounded-full bg-[#5fe3c9]"
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Icons (16px line icons, currentColor) ----------
function InboxIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 13h-7l-2 3h-2l-2-3H2" />
            <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
        </svg>
    );
}
function CalendarIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
    );
}
function MailIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-10 6L2 7" />
        </svg>
    );
}
function UsersIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
    );
}
function GridIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
        </svg>
    );
}
function OrbitIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="12" rx="10" ry="4" />
            <circle cx="12" cy="12" r="2.5" />
            <circle cx="22" cy="12" r="1.4" fill="currentColor" />
        </svg>
    );
}
function StackIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 2 7l10 5 10-5-10-5Z" />
            <path d="m2 17 10 5 10-5" />
            <path d="m2 12 10 5 10-5" />
        </svg>
    );
}
function ImageIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
    );
}
function MenuIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
    );
}
function LogoutIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
    );
}
